import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Menu, X, User, LogOut, UserPlus } from 'lucide-react'
import { useApi } from '../../services/api'
import { useCallback } from 'react'
import LoginModal from './LoginModal'
import RegisterModal from './RegisterModal'
import logoTander from '../../assets/WhatsApp_Image_2025-08-29_at_16.10.10-removebg-preview.png'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const { isAuthenticated, logout } = useApi()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { name: 'Potencial IA', href: '#funcionalidades' },
    { name: 'Planos Inteligentes', href: '#planos' },
    { name: 'Contato', href: '#contato' },
  ]

  const handleLogout = () => {
    logout()
    // Recarregar a página para atualizar o estado
    window.location.reload()
  }

  const { apiService } = useApi();
  const handleLoginSuccess = useCallback(async () => {
    setShowLoginModal(false)
    console.log('✅ [Navbar] Login realizado com sucesso, recarregando página...');
    // Apenas recarregar a página para atualizar o estado
    // O usuário pode clicar no link para ir ao app quando quiser
    window.location.reload();
  }, [apiService]);

  const handleRegisterSuccess = () => {
    setShowRegisterModal(false)
    // Após cadastro bem-sucedido, mostrar login
    setShowLoginModal(true)
  }

  const handleSwitchToLogin = () => {
    setShowRegisterModal(false)
    setShowLoginModal(true)
  }

  const handleSwitchToRegister = () => {
    setShowLoginModal(false)
    setShowRegisterModal(true)
  }

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-200 via-primary-500 to-accent-600">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2"
            >
              <img 
                src={logoTander} 
                alt="TANDER SMART" 
                className="w-24 h-17"
              />
              <span className="text-xl font-bold text-white">TANDER SMART</span>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  whileHover={{ y: -2 }}
                  className="text-gray-700 hover:text-primary-60 font-medium transition-colors duration-200 text-white"
                >
                  {item.name}
                </motion.a>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-4 text-white">
              {isAuthenticated ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-500 transition-colors duration-200 text-white font-bold"
                  >
                    <User className="w-5 h-5 text-white" />
                    <span>Minha Conta</span>
                  </motion.button>
                  <motion.button
                    onClick={handleLogout}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 text-gray-700 hover:text-red-500 transition-colors duration-200 text-white"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Sair</span>
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    onClick={() => setShowRegisterModal(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-outline flex items-center space-x-2 text-white"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Cadastrar</span>
                  </motion.button>
                  <motion.button
                    onClick={() => setShowLoginModal(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary"
                  >
                    Entrar
                  </motion.button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-700 hover:text-primary-500 transition-colors duration-200"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-200"
            >
              <div className="px-4 py-6 space-y-4">
                {navItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block text-gray-700 hover:text-primary-500 font-medium py-2"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </a>
                ))}
                
                {isAuthenticated ? (
                  <>
                    <button className="w-full text-left text-gray-700 hover:text-primary-500 font-medium py-2 flex items-center space-x-2">
                      <User className="w-5 h-5" />
                      <span>Minha Conta</span>
                    </button>
                    <button 
                      onClick={() => {
                        handleLogout()
                        setIsOpen(false)
                      }}
                      className="w-full text-left text-gray-700 hover:text-red-500 font-medium py-2 flex items-center space-x-2"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sair</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => {
                        setShowRegisterModal(true)
                        setIsOpen(false)
                      }}
                      className="btn-outline w-full flex items-center justify-center space-x-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Cadastrar</span>
                    </button>
                    <button 
                      onClick={() => {
                        setShowLoginModal(true)
                        setIsOpen(false)
                      }}
                      className="btn-primary w-full"
                    >
                      Entrar
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        onSwitchToRegister={handleSwitchToRegister}
      />
    </>
  )
}

export default Navbar
